import React from "react";
import { TextField } from "@mui/material";
import ColumnChildAutoComplete from "../columns/ColumnChildAutoComplete";
import NUMBER from "../components/NUMBER";
import { isNumericComponentType } from "./numericValidation";

export const buildColumns = ({
  data,
  columnRenderers,
  colItemOptions,
  quantitySelectOptions,
  handleRowUpdate,
  colTaxOptions,
  colAccountOptions,
  handleDeleteRow,
  handleAddRowAtIndex,
  handleRowClone,
  disableRate,
  priceSelect,
  formAction,
  defaultTableSelectedLocation,
  documentSelectTableData,
  textValue,
  rowErrors
}) => {
  const columns = [];

  columns.push({
    field: "SN",
    headerName: "No.",
    width: 70,
    sortable: true,
    disableColumnMenu: true,
    renderCell: (params) =>
      columnRenderers["col_row_index"]?.({ params }) || null,
  });

  // col_item column
  if (data?.fixcolumns) {
    columns.push(
      ...data.fixcolumns
        .filter(
          (item) =>
            String(item?.bVisible) === "true" && item.sColumnID === "col_item",
        )
        .map((item) => ({
          field: "col_item_name",
          headerName: item.sHeader,
          width: item.iWidth,
          renderCell: (params) =>
            columnRenderers[item.sColumnID]?.({
              params,
              item,
              handleRowUpdate,
              colItemOptions,
              data,
              priceSelect,
              formAction
            }),
        })),
    );
  }

  // child columns - only show if bVisible is true and sColDataAware conditions are met
  if (data?.child) {
    columns.push(
      ...data.child
        .filter((item) => {
          if (String(item?.inputtable?.bVisible) !== "true") return false;
          // Handle sColDataAware conditional visibility
          if (String(item?.inputtable?.sColDataAware) === "true") {
            const fieldTemplate = item?.inputtable?.sColDataAwareField || "";
            const fieldName = fieldTemplate.replace(/[{}]/g, "").trim();
            const expectedValue = item?.inputtable?.sColVisibleValue;
            const currentValue = textValue?.[fieldName];
            if (expectedValue && currentValue !== expectedValue) return false;
          }
          return true;
        })
        .map((item) => {
          const colId = item.inputtable.sColumnID;
          const headerLower = (item.inputtable.sHeader || "").toLowerCase();
          const sType = item?.component?.sType;
          const iDecimalPlaces = item?.component?.iDecimalPlaces;

          // Check if this column needs currency formatting
          const isCurrencyField = sType === "CURRENCY" ||
                                  colId.includes("balance") ||
                                  colId.includes("dBalance") ||
                                  colId.includes("amount") ||
                                  colId.includes("payment") ||
                                  headerLower.includes("balance") ||
                                  headerLower.includes("amount") ||
                                  headerLower.includes("payment");
          const decimalPlaces = iDecimalPlaces !== undefined ? iDecimalPlaces : (isCurrencyField ? 2 : undefined);

          return {
            field: colId,
            headerName: item.inputtable.sHeader,
            width: item.inputtable.iWidth,
            renderCell: (params) => {
              // Try custom renderer first
              const customRenderer = columnRenderers[colId]?.({
                params,
                item,
                data,
                formAction,
                defaultTableSelectedLocation,
                quantitySelectOptions,
                handleRowUpdate,
                documentSelectTableData
              });
              if (customRenderer) return customRenderer;

              // Handle AUTOCOMPLETE type child columns
              if (sType === "AUTOCOMPLETE") {
                const cellError = rowErrors?.[params?.row?.id]?.[colId];
                return (
                  <ColumnChildAutoComplete
                    params={params}
                    item={item}
                    handleRowUpdate={handleRowUpdate}
                    cellError={cellError}
                  />
                );
              }

              // Handle TEXTFIELD type child columns
              if (sType === "TEXTFIELD") {
                const sName = item?.component?.sName || colId;
                const fieldId = params?.row?._fieldIds?.[colId] || sName;
                const cellError = rowErrors?.[params?.row?.id]?.[colId];
                return (
                  <TextField
                    id={`${fieldId}-${params?.row?.id}`}
                    name={sName}
                    value={params?.row?.[colId] ?? ""}
                    onChange={(e) => {
                      handleRowUpdate({
                        fieldName: colId,
                        value: e.target.value,
                        rowId: params?.row?.id,
                      });
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    disabled={item?.component?.sProps?.disabled}
                    placeholder={item?.component?.sPlaceHolder}
                    error={!!cellError}
                    helperText={cellError || ""}
                    size="medium"
                    fullWidth
                  />
                );
              }

              if (isNumericComponentType(sType)) {
                const cellError = rowErrors?.[params?.row?.id]?.[colId];
                return (
                  <NUMBER
                    params={params}
                    item={item}
                    handleRowUpdate={handleRowUpdate}
                    rowError={cellError}
                  />
                );
              }

              const fieldId = params?.row?._fieldIds?.[colId] || item?.component?.sName || colId;
              const value = params?.row?.[colId] ?? "";

              // Apply currency formatting if needed
              let displayValue = value;
              if (isCurrencyField && decimalPlaces !== undefined && value !== "" && value !== null && value !== undefined) {
                const numValue = parseFloat(String(value).replace(/,/g, ""));
                if (!isNaN(numValue)) {
                  displayValue = numValue.toLocaleString("en-US", {
                    minimumFractionDigits: decimalPlaces,
                    maximumFractionDigits: decimalPlaces
                  });
                }
              }

              return (
                <p id={`${fieldId}-${params?.row?.id}`} style={{ margin: 0 }}>
                  <span>{displayValue}</span>
                </p>
              );
            },
          };
        }),
    );
  }

  // fixcolumns - only show if bVisible is true
  if (data?.fixcolumns) {
    columns.push(
      ...data.fixcolumns
        .filter(
          (item) =>
            String(item?.bVisible) === "true" &&
            item.sColumnID !== "col_item" &&
            Number(item?.inputType?.data?.bPayload) === 1,
        )
        .map((item) => {
          const iDecimalPlaces = item?.inputType?.component?.iDecimalPlaces;
          const sType = item?.inputType?.component?.sType;
          const hasCustomRenderer = !!columnRenderers[item.sColumnID];
          const headerLower = (item.sHeader || "").toLowerCase();

          // Check if this column should have number formatting
          const isCurrencyField = sType === "CURRENCY" ||
                                  headerLower.includes("balance") ||
                                  headerLower.includes("amount") ||
                                  headerLower.includes("payment");

          // Use iDecimalPlaces from config, default to 2 for currency fields
          const decimalPlaces = iDecimalPlaces !== undefined ? iDecimalPlaces : (isCurrencyField ? 2 : undefined);
          const needsNumberFormatting = isCurrencyField && decimalPlaces !== undefined;

          const columnDef = {
            field: item.sColumnID,
            headerName: item.sHeader,
            width: item.iWidth,
          };

          // If there's a custom renderer, use it
          if (hasCustomRenderer) {
            columnDef.renderCell = (params) =>
              columnRenderers[item.sColumnID]({
                params,
                item,
                quantitySelectOptions,
                handleRowUpdate,
                colTaxOptions,
                colAccountOptions,
                disableRate,
                formAction,
                documentSelectTableData
              });
          } else if (isNumericComponentType(sType) && Number(item.bEditable) === 1) {
            columnDef.renderCell = params => (
              <NUMBER
                params={params}
                item={item}
                handleRowUpdate={handleRowUpdate}
                rowError={rowErrors?.[params?.row?.id]?.[item.sColumnID]}
              />
            );
          } else if (needsNumberFormatting) {
            columnDef.renderCell = (params) => {
              const value = params.value;
              if (value === undefined || value === null || value === "") {
                return (0).toLocaleString(undefined, {
                  minimumFractionDigits: decimalPlaces,
                  maximumFractionDigits: decimalPlaces
                });
              }
              const numValue = Number(String(value).replace(/,/g, ""));
              return numValue.toLocaleString(undefined, {
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces
              });
            };
          }

          return columnDef;
        }),
    );
  }

  // actions
  const showActions =
    data?.component?.options?.enableRowDelete ||
    data?.component?.options?.enableRowClone ||
    data?.component?.options?.enableRowAdd;
  if (showActions && data?.component?.options?.mode === "DEFAULT") {
    columns.push({
      field: "col_actions",
      headerName: "",
      width: 100,
      renderCell: (params) => columnRenderers["col_actions"]?.({
        params,
        data,
        handleDeleteRow,
        handleAddRowAtIndex,
        handleRowClone,
        formAction
      }),
    });
  }

  return columns;
};
